import { supabase } from "@/integrations/supabase/client";

export type SavedGameScope = "future" | "next_only" | "special";
export type PoolScope = "next_only" | "future_5" | "future_10" | "future_all" | "special";

export type SpecialDraw = {
  id: string;
  lottery_id: string;
  lottery_slug: string;
  lottery_name: string;
  contest_number: number | null;
  draw_date: string;
  estimated_prize: number | null;
  special_name: string | null;
  special_slug: string | null;
};

/** Nomes oficiais dos sorteios especiais por loteria. */
export const SPECIAL_DRAWS: Record<string, { slug: string; name: string }> = {
  "mega-sena": { slug: "mega-da-virada", name: "Mega da Virada" },
  lotofacil: { slug: "lotofacil-da-independencia", name: "Lotofácil da Independência" },
  quina: { slug: "quina-de-sao-joao", name: "Quina de São João" },
  "dupla-sena": { slug: "dupla-sena-de-pascoa", name: "Dupla Sena de Páscoa" },
};

/** Lista os sorteios especiais anunciados e ainda não realizados. */
export async function getActiveSpecials(): Promise<SpecialDraw[]> {
  const { data } = await supabase.rpc("get_active_specials");
  return (data ?? []) as SpecialDraw[];
}

/** Sorteio especial ativo de uma loteria (o mais próximo), se houver. */
export async function getActiveSpecial(lotterySlug: string): Promise<SpecialDraw | null> {
  const all = await getActiveSpecials();
  return all.find((s) => s.lottery_slug === lotterySlug) ?? null;
}

export function formatSpecialDate(d: string): string {
  return new Date(d + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
}

export type ScopeValue = {
  scope: SavedGameScope | PoolScope;
  from: number | null;
  until: number | null;
};

/** Próximo concurso = MAX(contest_number) + 1 da loteria. */
export async function getNextContestNumber(lotterySlug: string): Promise<number> {
  const { data: lottery } = await supabase
    .from("lotteries")
    .select("id")
    .eq("slug", lotterySlug)
    .maybeSingle();
  if (!lottery) return 1;
  const { data } = await supabase
    .from("draws")
    .select("contest_number")
    .eq("lottery_id", lottery.id)
    .order("contest_number", { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data?.contest_number ?? 0) + 1;
}

export function savedGameScopeToRange(
  scope: SavedGameScope,
  nextContest: number,
): { from: number | null; until: number | null } {
  if (scope === "future") return { from: nextContest, until: null };
  return { from: nextContest, until: nextContest };
}

export function poolScopeToRange(
  scope: PoolScope,
  nextContest: number,
): { from: number | null; until: number | null } {
  if (scope === "future_all") return { from: nextContest, until: null };
  if (scope === "next_only") return { from: nextContest, until: nextContest };
  if (scope === "future_5") return { from: nextContest, until: nextContest + 4 };
  return { from: nextContest, until: nextContest + 9 };
}

export function describeSavedGameScope(
  scope: SavedGameScope | string | null | undefined,
  from: number | null | undefined,
  until: number | null | undefined,
  specialName?: string | null,
): string {
  if (scope === "special") return specialName ? `Sorteio especial — ${specialName}` : "Sorteio especial";
  if (scope === "next_only") return `Apenas o #${from ?? "?"}`;
  return `Futuros (a partir do #${from ?? "?"})`;
}

export function describePoolScope(
  scope: PoolScope | string | null | undefined,
  from: number | null | undefined,
  until: number | null | undefined,
  specialName?: string | null,
): string {
  if (from != null && until === from) return `Concurso #${from}${scope === "special" && specialName ? ` — ${specialName}` : ""}`;
  if (scope === "special") return specialName ? `Sorteio especial — ${specialName}` : "Sorteio especial";
  if (scope === "next_only") return `Concurso #${from ?? "?"}`;
  if (scope === "future_5") return `Próximos 5 (#${from} a #${until})`;
  if (scope === "future_10") return `Próximos 10 (#${from} a #${until})`;
  return `Todos os futuros (a partir do #${from ?? "?"})`;
}